import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, PackageSearch } from "lucide-react";
import { inr } from "./BillingParts";
import ItemPickerSheet from "./ItemPickerSheet";

const GST_RATES = [0, 5, 12, 18, 28];
const emptyLine = () => ({ description: "", hsn: "", qty: 1, unit: "Nos", rate: "", gst_rate: 18, item_id: null });
const today = () => new Date().toISOString().slice(0, 10);
const fromItem = (it, qty = 1) => ({ description: it.name, hsn: it.hsn || "", qty, unit: it.unit || "Nos", rate: it.sale_price, gst_rate: it.gst_rate ?? 18, item_id: it.id });
const LBL = "text-[11px] tracking-[0.14em] uppercase font-semibold";
const MINI = "text-[10px] tracking-[0.1em] uppercase text-zinc-500 font-semibold";

function LineFields({ it, idx, withGst, stockItems, upd, pickItem, amt, remove, canRemove }) {
  const descBox = (
    <div className="flex gap-1 min-w-0">
      <Input data-testid={`inv-desc-${idx}`} value={it.description} onChange={(e) => upd(idx, "description", e.target.value)} placeholder="Item / service" className="h-9 md:h-8 text-sm md:text-xs rounded-none flex-1 min-w-0" />
      {stockItems.length ? (
        <select data-testid={`inv-item-${idx}`} value={it.item_id || ""} onChange={(e) => pickItem(idx, e.target.value)} title="Pick from items" className="h-9 md:h-8 w-9 md:w-7 text-xs border border-zinc-300 bg-white shrink-0">
          <option value="">—</option>
          {stockItems.map(s => <option key={s.id} value={s.id}>{s.name}{s.type === "product" ? ` (${s.total_stock} ${s.unit})` : ""}</option>)}
        </select>
      ) : null}
    </div>
  );
  const hsn = <Input data-testid={`inv-hsn-${idx}`} value={it.hsn} onChange={(e) => upd(idx, "hsn", e.target.value)} placeholder="HSN" className="h-9 md:h-8 text-sm md:text-xs rounded-none" />;
  const qty = <Input data-testid={`inv-qty-${idx}`} type="number" inputMode="decimal" min="0" step="any" value={it.qty} onChange={(e) => upd(idx, "qty", e.target.value)} className="h-9 md:h-8 text-sm md:text-xs rounded-none tabular" />;
  const rate = <Input data-testid={`inv-rate-${idx}`} type="number" inputMode="decimal" min="0" step="any" value={it.rate} onChange={(e) => upd(idx, "rate", e.target.value)} placeholder="0" className="h-9 md:h-8 text-sm md:text-xs rounded-none tabular" />;
  const gst = withGst ? (
    <select data-testid={`inv-gst-${idx}`} value={it.gst_rate} onChange={(e) => upd(idx, "gst_rate", Number(e.target.value))} className="h-9 md:h-8 w-full text-sm md:text-xs border border-zinc-300 bg-white px-1">
      {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
    </select>
  ) : null;
  const del = <button type="button" data-testid={`inv-remove-${idx}`} onClick={remove} disabled={!canRemove} className="p-1.5 text-zinc-400 hover:text-rose-600 disabled:opacity-30"><Trash2 size={15} /></button>;

  return (
    <>
      {/* Mobile card */}
      <div className="md:hidden p-3 space-y-2 border-t border-zinc-200 bg-white" data-testid={`inv-line-${idx}`}>
        <div className="flex items-center justify-between">
          <span className={MINI}>Item {idx + 1}</span>
          {del}
        </div>
        {descBox}
        <div className="grid grid-cols-3 gap-2">
          <div><span className={MINI}>Qty</span>{qty}</div>
          <div><span className={MINI}>Rate</span>{rate}</div>
          <div><span className={MINI}>HSN/SAC</span>{hsn}</div>
          {withGst ? <div><span className={MINI}>GST%</span>{gst}</div> : null}
          <div className={withGst ? "col-span-2" : "col-span-3"}>
            <span className={MINI}>Amount</span>
            <div className="h-9 flex items-center justify-end px-2 bg-zinc-50 border border-zinc-200 text-sm tabular font-semibold">{inr(amt)}</div>
          </div>
        </div>
      </div>
      {/* Desktop row */}
      <div className={`hidden md:grid ${withGst ? "grid-cols-[1fr_70px_60px_80px_70px_90px_28px]" : "grid-cols-[1fr_70px_60px_80px_90px_28px]"} gap-1 px-2 py-1 items-center border-t border-zinc-100`}>
        {descBox}{hsn}{qty}{rate}{gst}
        <div className="text-right text-xs tabular font-medium">{inr(amt)}</div>
        {del}
      </div>
    </>
  );
}

export default function InvoiceForm({ open, onOpenChange, onSaved, invoice, profile, quote = false, endpoint = null, extraBody = {} }) {
  const [customers, setCustomers] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [scan, setScan] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [withGst, setWithGst] = useState(true);
  const [issueDate, setIssueDate] = useState(today());
  const [dueDate, setDueDate] = useState("");
  const [items, setItems] = useState([emptyLine()]);
  const [notes, setNotes] = useState("");
  const [roundOff, setRoundOff] = useState(true);
  const [supplyType, setSupplyType] = useState("auto");
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/billing/customers").then(r => setCustomers(r.data));
    api.get("/inventory/items").then(r => setStockItems(r.data)).catch(() => setStockItems([]));
    if (invoice) {
      setCustomerId(invoice.customer_id); setWithGst(invoice.with_gst); setIssueDate(invoice.issue_date); setDueDate(invoice.due_date || "");
      setItems(invoice.items.map(i => ({ description: i.description, hsn: i.hsn || "", qty: i.qty, unit: i.unit || "Nos", rate: i.rate, gst_rate: i.gst_rate, item_id: i.item_id || null })));
      setNotes(invoice.notes || ""); setRoundOff(invoice.round_off !== false); setSupplyType(invoice.supply_type && invoice.supply_type !== "auto" ? invoice.supply_type : "auto");
    } else {
      setCustomerId(""); setWithGst(!!profile?.gstin); setIssueDate(today()); setDueDate(""); setItems([emptyLine()]); setNotes(""); setRoundOff(true); setSupplyType("auto");
    }
  }, [open, invoice, profile]);

  const cust = customers.find(c => c.id === customerId);
  const autoIntra = !!(cust && profile?.state_code && (cust.state_code || profile.state_code) === profile.state_code);
  const intra = withGst && (supplyType === "auto" ? autoIntra : supplyType === "intra");

  const totals = useMemo(() => {
    let sub = 0, tax = 0;
    items.forEach(i => { const t = (Number(i.qty) || 0) * (Number(i.rate) || 0); sub += t; if (withGst) tax += t * (Number(i.gst_rate) || 0) / 100; });
    const gross = sub + tax; const total = roundOff ? Math.round(gross) : Math.round(gross * 100) / 100;
    return { sub, tax, total, ro: total - gross };
  }, [items, withGst, roundOff]);

  const upd = (idx, k, v) => setItems(items.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  const pickItem = (idx, id) => { const it = stockItems.find(s => s.id === id); setItems(items.map((l, i) => i === idx ? (it ? fromItem(it, l.qty || 1) : { ...l, item_id: null }) : l)); };
  const addScanned = () => {
    const code = scan.trim(); if (!code) return;
    const it = stockItems.find(s => s.barcode === code || (s.sku && s.sku.toLowerCase() === code.toLowerCase()) || s.name.toLowerCase() === code.toLowerCase());
    if (!it) { toast.error("No item found", { description: code }); setScan(""); return; }
    const existing = items.findIndex(l => l.item_id === it.id);
    if (existing >= 0) setItems(items.map((l, i) => i === existing ? { ...l, qty: (Number(l.qty) || 0) + 1 } : l));
    else { const blankIdx = items.findIndex(l => !l.description.trim()); setItems(blankIdx >= 0 ? items.map((l, i) => i === blankIdx ? fromItem(it) : l) : [...items, fromItem(it)]); }
    setScan("");
  };
  const addItem = (it) => {
    const existing = items.findIndex(l => l.item_id === it.id);
    if (existing >= 0) return setItems(items.map((l, i) => i === existing ? { ...l, qty: (Number(l.qty) || 0) + 1 } : l));
    const blankIdx = items.findIndex(l => !l.description.trim());
    setItems(blankIdx >= 0 ? items.map((l, i) => i === blankIdx ? fromItem(it) : l) : [...items, fromItem(it)]);
  };
  const setItemQty = (it, n) => {
    if (n <= 0) { const rest = items.filter(l => l.item_id !== it.id); return setItems(rest.length ? rest : [emptyLine()]); }
    setItems(items.map(l => l.item_id === it.id ? { ...l, qty: n } : l));
  };

  const save = async () => {
    if (!customerId) return toast.error("Select a customer");
    if (items.some(i => !i.description.trim())) return toast.error("Every line needs a description");
    setBusy(true);
    try {
      const body = { customer_id: customerId, with_gst: withGst, issue_date: issueDate, due_date: dueDate || null, notes, round_off: roundOff,
        supply_type: withGst && supplyType !== "auto" ? supplyType : null, ...(quote ? { valid_till: dueDate || null } : {}), ...extraBody,
        items: items.map(i => ({ ...i, qty: Number(i.qty) || 0, rate: Number(i.rate) || 0, gst_rate: Number(i.gst_rate) || 0 })) };
      const base = endpoint || (quote ? "/biz/quotes" : "/billing/invoices");
      const { data } = invoice ? await api.put(`${base}/${invoice.id}`, body) : await api.post(base, body);
      toast.success(invoice ? (quote ? "Quote updated" : "Bill updated") : `${quote ? "Quote" : "Bill"} ${data.invoice_number} created`);
      onSaved?.(data); onOpenChange(false);
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  const title = invoice ? `Edit ${invoice.invoice_number}` : endpoint ? "New delivery challan" : quote ? "New quotation" : "New sell bill";
  const saveLabel = busy ? "Saving…" : invoice ? "Save changes" : endpoint ? "Create challan" : quote ? "Create quote" : "Create bill";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="invoice-form"
        className="w-screen h-[100dvh] max-w-none max-h-none inset-0 left-0 top-0 translate-x-0 translate-y-0 rounded-none p-0 gap-0 flex flex-col overflow-hidden md:w-[calc(100%-2rem)] md:h-auto md:max-w-3xl md:max-h-[92vh] md:left-[50%] md:top-[50%] md:translate-x-[-50%] md:translate-y-[-50%]">
        <DialogHeader className="px-4 pt-4 pb-3 md:px-6 md:pt-6 border-b border-zinc-100 shrink-0 text-left pr-12">
          <DialogTitle className="font-heading tracking-tight text-lg md:text-xl">{title}</DialogTitle>
          <DialogDescription className="text-xs md:text-sm">{withGst ? "Tax invoice — GST will be added per line" : "Bill without GST"}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-4 md:px-6 space-y-4 pb-28 md:pb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="col-span-2">
              <Label className={LBL}>Customer</Label>
              <select data-testid="inv-customer" value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="w-full h-10 md:h-9 text-sm border border-zinc-300 px-2 bg-white">
                <option value="">Select customer…</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name}{c.gstin ? ` · ${c.gstin}` : ""}</option>)}
              </select>
            </div>
            <div className="min-w-0"><Label className={LBL}>Date</Label><Input data-testid="inv-date" type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className="rounded-none h-10 md:h-9 w-full min-w-0" /></div>
            <div className="min-w-0"><Label className={LBL}>{quote ? "Valid till" : "Due date"}</Label><Input data-testid="inv-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="rounded-none h-10 md:h-9 w-full min-w-0" /></div>
          </div>

          <div className="space-y-2">
            <div className="grid grid-cols-2 border border-zinc-200 p-1 w-full md:w-fit md:flex" data-testid="inv-gst-toggle">
              <button type="button" data-testid="inv-with-gst" onClick={() => setWithGst(true)} className={`text-xs font-semibold px-3 py-2 ${withGst ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>With GST</button>
              <button type="button" data-testid="inv-without-gst" onClick={() => setWithGst(false)} className={`text-xs font-semibold px-3 py-2 ${!withGst ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>Without GST</button>
            </div>
            {withGst ? (
              <div className="border border-zinc-200 p-1 w-full md:w-fit" data-testid="inv-supply-toggle">
                <span className={`${MINI} block px-2 pt-1 pb-1.5 md:hidden`}>Supply type</span>
                <div className="grid grid-cols-3 md:flex md:items-center gap-1">
                  <span className={`${MINI} hidden md:inline pl-2`}>Supply</span>
                  {[["auto", "Auto", "Auto"], ["intra", "Intra-state", "Intra-state (CGST+SGST)"], ["inter", "Inter-state", "Inter-state (IGST)"]].map(([k, s, l]) => (
                    <button key={k} type="button" data-testid={`inv-supply-${k}`} onClick={() => setSupplyType(k)} className={`text-xs font-semibold px-2 md:px-3 py-2 md:py-1.5 ${supplyType === k ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}><span className="md:hidden">{s}</span><span className="hidden md:inline">{l}</span></button>
                  ))}
                </div>
              </div>
            ) : null}
            {withGst && cust ? <p className="text-[11px] text-zinc-500 leading-snug" data-testid="inv-supply-hint">{intra ? "Applying CGST + SGST (same state)" : "Applying IGST (other state)"}{supplyType === "auto" ? ` · auto from state codes ${profile?.state_code || "?"} → ${cust.state_code || profile?.state_code || "?"}` : ""}</p> : null}
          </div>

          <div className="border border-zinc-200">
            {stockItems.length ? (
              <button type="button" data-testid="inv-open-picker" onClick={() => setPicker(true)} className="md:hidden w-full flex items-center justify-center gap-2 py-3 bg-zinc-900 text-white text-sm font-semibold active:bg-zinc-800"><PackageSearch size={16} /> Pick items from stock</button>
            ) : null}
            {stockItems.length ? (
              <div className="hidden md:flex items-center gap-2 px-2 py-2 bg-white border-b border-zinc-200">
                <Input data-testid="inv-scan" value={scan} onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addScanned(); } }} placeholder="Scan barcode / SKU / item name" className="h-9 md:h-8 text-sm md:text-xs rounded-none min-w-0" />
                <Button type="button" data-testid="inv-scan-add" onClick={addScanned} variant="outline" className="h-9 md:h-8 rounded-none text-xs shrink-0">Add</Button>
              </div>
            ) : null}
            <div className={`hidden md:grid ${withGst ? "grid-cols-[1fr_70px_60px_80px_70px_90px_28px]" : "grid-cols-[1fr_70px_60px_80px_90px_28px]"} gap-1 bg-zinc-50 px-2 py-1.5 ${MINI}`}>
              <span>Description</span><span>HSN/SAC</span><span>Qty</span><span>Rate</span>{withGst ? <span>GST%</span> : null}<span className="text-right">Amount</span><span />
            </div>
            <div className={`md:hidden bg-zinc-50 px-3 py-1.5 ${MINI}`}>Items ({items.length})</div>
            {items.map((it, idx) => {
              const t = (Number(it.qty) || 0) * (Number(it.rate) || 0); const amt = withGst ? t * (1 + (Number(it.gst_rate) || 0) / 100) : t;
              return <LineFields key={idx} it={it} idx={idx} withGst={withGst} stockItems={stockItems} upd={upd} pickItem={pickItem} amt={amt} canRemove={items.length > 1} remove={() => setItems(items.filter((_, i) => i !== idx))} />;
            })}
            <button type="button" data-testid="inv-add-line" onClick={() => setItems([...items, emptyLine()])} className="w-full text-xs font-semibold py-3 md:py-2 border-t border-zinc-200 hover:bg-zinc-50 active:bg-zinc-100 flex items-center justify-center gap-1"><Plus size={14} /> Add line</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-zinc-200 bg-zinc-50 p-3 text-sm space-y-1 tabular order-first md:order-last" data-testid="inv-totals">
              <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span>{inr(totals.sub)}</span></div>
              {withGst ? <div className="flex justify-between"><span className="text-zinc-500">{intra ? "CGST + SGST" : "IGST"}</span><span>{inr(totals.tax)}</span></div> : null}
              {roundOff && Math.abs(totals.ro) > 0.004 ? <div className="flex justify-between"><span className="text-zinc-500">Round off</span><span>{totals.ro > 0 ? "+" : ""}{totals.ro.toFixed(2)}</span></div> : null}
              <div className="flex justify-between font-heading font-bold text-base pt-1 border-t border-zinc-200"><span>Total</span><span data-testid="inv-total">{inr(totals.total)}</span></div>
            </div>
            <div>
              <Label className={LBL}>Notes (printed)</Label>
              <Textarea data-testid="inv-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="text-sm" />
              <label className="flex items-center gap-2 mt-2 text-xs"><input type="checkbox" data-testid="inv-roundoff" checked={roundOff} onChange={(e) => setRoundOff(e.target.checked)} className="accent-zinc-900 h-4 w-4" /> Round off total</label>
            </div>
          </div>
        </div>

        <div className="shrink-0 border-t border-zinc-200 bg-white px-4 py-3 md:px-6 flex items-center gap-3 fixed bottom-0 left-0 right-0 md:static md:justify-end" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
          <div className="md:hidden flex-1 min-w-0">
            <div className={MINI}>Total</div>
            <div className="font-heading font-bold text-lg tabular leading-tight truncate" data-testid="inv-total-sticky">{inr(totals.total)}</div>
          </div>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none">Cancel</Button>
          <Button data-testid="inv-save" onClick={save} disabled={busy} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white h-11 md:h-10 px-5">{saveLabel}</Button>
        </div>
        <ItemPickerSheet open={picker} onOpenChange={setPicker} stockItems={stockItems} lines={items} onAdd={addItem} onSetQty={setItemQty} onFavorite={(id, fav) => setStockItems(stockItems.map(s => s.id === id ? { ...s, favorite: fav } : s))} />
      </DialogContent>
    </Dialog>
  );
}
